import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { useUIStore } from '@/store/uiStore'
import { useAuth } from '@/hooks/useAuth'
import { ForgotPasswordModal } from './ForgotPasswordModal'

// Validation schemas
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

function LoginModal() {
  const navigate = useNavigate()
  const { activeModal, closeModal, openModal } = useUIStore()
  const { login } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [shakeForm, setShakeForm] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Focus email input when modal opens
  useEffect(() => {
    if (activeModal === 'login') {
      setTimeout(() => emailInputRef.current?.focus(), 100)
    }
  }, [activeModal])

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true)
    try {
      await login(data.email, data.password)
      reset()
    } catch {
      // Error handled in useAuth — trigger shake
      setShakeForm(true)
      setTimeout(() => setShakeForm(false), 400)
    } finally {
      setIsSubmitting(false)
    }
  }

  const switchToSignup = () => {
    closeModal()
    navigate('/signup')
  }

  return (
    <Modal isOpen={activeModal === 'login'} onClose={closeModal} title="Log In">
      <form onSubmit={handleSubmit(onSubmit)} className={`space-y-4 ${shakeForm ? 'animate-shake' : ''}`}>
        <Input
          label="Email"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
          ref={(e) => {
            register('email').ref(e)
            ;(emailInputRef as React.MutableRefObject<HTMLInputElement | null>).current = e
          }}
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <Button type="submit" fullWidth loading={isSubmitting}>
            Log In
          </Button>
        </div>

        <p className="text-center text-sm">
          <button
            type="button"
            onClick={() => { closeModal(); setTimeout(() => openModal('forgot-password'), 100) }}
            className="link text-slate-500"
          >
            Forgot your password?
          </button>
        </p>

        <p className="text-center text-sm text-slate-600">
          Don&apos;t have an account?{' '}
          <button type="button" onClick={switchToSignup} className="link">
            Sign up
          </button>
        </p>

        {/* Migration notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
          <p className="text-sm font-medium text-amber-800 mb-1">
            Returning from our old site?
          </p>
          <p className="text-xs text-amber-700">
            We've upgraded to a brand new platform! You'll need to reset your password to log in.{' '}
            <button
              type="button"
              onClick={() => { closeModal(); setTimeout(() => openModal('forgot-password'), 100) }}
              className="font-semibold underline hover:no-underline"
            >
              Reset password
            </button>
          </p>
        </div>
      </form>
    </Modal>
  )
}

export function AuthModals() {
  return (
    <>
      <LoginModal />
      <ForgotPasswordModal />
    </>
  )
}
